import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast, showErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'

export interface CancelRequestedContactsParams {
  requestedContactId: string
}

const cancelRequestedContacts = async ({
  requestedContactId,
}: CancelRequestedContactsParams): Promise<undefined> => {
  const endpoint = `/contact-requests/${requestedContactId}`
  const { data: responseData } = await api.delete<undefined>(endpoint)
  return responseData
}

export const useCancelRequestedContacts = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<undefined, Error, CancelRequestedContactsParams>({
    mutationFn: cancelRequestedContacts,
    onSuccess: async () => {
      showSuccessToast(t('toast.contactRequestCancelled'))
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['suggested-contacts'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['contact-requests'],
        }),
      ])
    },
    // On the mutation itself, so it is said even once the screen is closed.
    // A refusal mostly means the request was answered meanwhile: read the
    // waiting requests again so the button and the list follow.
    onError: async (error) => {
      const response = (error as { response?: { status?: number; data?: { code?: string } } })
        .response
      // Accepted, refused or already cancelled: say it plainly, as when
      // answering a request that is gone.
      if (
        response?.status === 404 ||
        response?.data?.code === 'Networking.ContactRequest.CannotCancelNonPending'
      ) {
        showErrorToast(t('errors.title'), t('notifications.requestGone'))
      } else {
        showApiErrorToast(t, error)
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contact-requests'] }),
        // The request may have been accepted meanwhile: the person is then a
        // contact, while their profile was cached as a stranger's. Read both
        // again, or the page would offer « Ajouter » for someone already added.
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'profile' && query.queryKey[1] !== 'me',
        }),
      ])
    },
  })
}
